(() => {
  if (window.KeithSectionLibraryLoaded) {
    if (window.KeithSectionLibraryBoot) window.KeithSectionLibraryBoot(document);
    return;
  }
  window.KeithSectionLibraryLoaded = true;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const money = (cents, format) => {
    const raw = Number(cents || 0);
    const value = (raw / 100).toFixed(2);
    if (!format) return `$${value}`;
    return format
      .replace(/\{\{\s*amount\s*\}\}/g, value)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(raw / 100).toString())
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, value.replace('.', ','));
  };

  const emit = (name, detail = {}) => {
    document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
  };

  const normalizeOptionValue = value =>
    String(value || '')
      .toLowerCase()
      .replace(/\([^)]*\)/g, '')
      .replace(/[^a-z0-9\u3040-\u30ff\u3400-\u9fff]+/gi, '')
      .trim();

  async function openThemeCartDrawer(item) {
    const cartPageUrl = window.theme?.routes?.cart ||
      `${window.Shopify?.routes?.root || '/'}cart`;
    const rootUrl = window.theme?.routes?.root_url || window.Shopify?.routes?.root || '/';
    const drawer = document.querySelector('[data-drawer="drawer-cart"]');
    if (!drawer) {
      window.location.assign(cartPageUrl);
      return false;
    }

    // This timeout protects read requests only. Never retry the successful add.
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    const readSection = async name => {
      const bundled = item?.sections?.[name];
      if (typeof bundled === 'string' && bundled.trim()) return bundled;
      const response = await fetch(`${rootUrl}?section_id=${encodeURIComponent(name)}`, {
        headers: { Accept: 'text/html' },
        signal: controller.signal
      });
      if (!response.ok) throw new Error(`Unable to refresh ${name}.`);
      return response.text();
    };

    try {
      const readCart = async () => {
        const response = await fetch(`${cartPageUrl}.js`, {
          headers: { Accept: 'application/json' },
          signal: controller.signal
        });
        if (!response.ok) throw new Error('Unable to refresh cart data.');
        const cart = await response.json();
        if (!cart || !Number.isInteger(cart.item_count) ||
            !Number.isFinite(cart.total_price)) {
          throw new Error('Invalid cart response.');
        }
        return cart;
      };

      // Missing bundled sections fall back to concurrent reads, not another add.
      const [cart, itemsHTML, subtotalHTML] = await Promise.all([
        readCart(),
        readSection('api-cart-items'),
        readSection('api-cart-subtotal')
      ]);
      const detail = {
        cart,
        sections: {
          'api-cart-items': itemsHTML,
          'api-cart-subtotal': subtotalHTML
        },
        rendered: false,
        error: null
      };

      // The paired Pipeline listener renders synchronously and acknowledges it.
      drawer.dispatchEvent(new CustomEvent('keith:cart:render', { detail }));
      if (!detail.rendered) {
        throw detail.error || new Error('The cart renderer is not ready.');
      }

      emit('cart:updated', { cart, item });
      emit('cart:refresh', { cart, item });
      if (!drawer.classList.contains('drawer--visible')) {
        drawer.dispatchEvent(new CustomEvent('theme:drawer:open', { bubbles: false }));
      }
      return true;
    } catch (error) {
      controller.abort();
      console.warn('[Keith] Cart added; opening the full cart:', error);
      window.location.assign(cartPageUrl);
      return false;
    } finally {
      clearTimeout(timeout);
    }
  }

  function galleryScrollTo(gallery, item, behavior = 'smooth') {
    if (!gallery || !item) return;
    const left = item.offsetLeft - Math.max(0, (gallery.clientWidth - item.offsetWidth) / 2);
    gallery.scrollTo({ left: Math.max(0, left), behavior });
  }

  function initProduct(root) {
    if (!root || root.dataset.k2Ready === '1') return;
    root.dataset.k2Ready = '1';

    let product = {};
    try {
      product = JSON.parse(root.querySelector('[data-k2-product-json]')?.textContent || '{}');
    } catch (e) {
      return;
    }

    const variants = product.variants || [];
    const form = root.querySelector('[data-k2-product-form]');
    const idInput = root.querySelector('[data-k2-variant-id]');
    const mainAtc = root.querySelector('[data-k2-main-atc]');
    const price = root.querySelector('[data-k2-price]');
    const compare = root.querySelector('[data-k2-compare]');
    const save = root.querySelector('[data-k2-save]');
    const fmt = root.dataset.moneyFormat;

    const radios = [...root.querySelectorAll('[data-k2-option-radio]')];
    const selects = [...root.querySelectorAll('[data-k2-option-select]')];

    const urlVariantId = new URL(window.location.href).searchParams.get('variant');
    const preferredOptionPosition = Math.max(0, Number(root.dataset.preferredOptionPosition || 1) - 1);
    const preferredOptionValue = root.dataset.preferredOptionValue || '';

    let currentVariant =
      variants.find(v => String(v.id) === String(urlVariantId)) ||
      (
        preferredOptionValue
          ? variants.find(v =>
              v.available &&
              normalizeOptionValue(v.options?.[preferredOptionPosition]) === normalizeOptionValue(preferredOptionValue)
            ) ||
            variants.find(v =>
              normalizeOptionValue(v.options?.[preferredOptionPosition]) === normalizeOptionValue(preferredOptionValue)
            ) ||
            variants.find(v =>
              normalizeOptionValue(v.options?.[preferredOptionPosition]).startsWith(normalizeOptionValue(preferredOptionValue)) ||
              normalizeOptionValue(preferredOptionValue).startsWith(normalizeOptionValue(v.options?.[preferredOptionPosition]))
            )
          : null
      ) ||
      variants.find(v => String(v.id) === String(idInput?.value)) ||
      variants[0] ||
      null;

    let isAdding = false;

    function selections() {
      const maxOptions = product.options?.length || 0;
      const out = new Array(maxOptions).fill(null);
      radios.filter(r => r.checked).forEach(r => { out[Number(r.dataset.position) - 1] = r.value; });
      selects.forEach(s => { out[Number(s.dataset.position) - 1] = s.value; });
      return out;
    }

    function getVariant() {
      const selected = selections();
      return variants.find(v => selected.every((value, idx) => value == null || v.options[idx] === value));
    }

    function updateOptionStates() {
      const selected = selections();
      radios.forEach(input => {
        const pos = Number(input.dataset.position) - 1;
        const wrapper = input.closest('.k2-option-card');
        const exists = variants.some(v =>
          v.options[pos] === input.value &&
          selected.every((sv, idx) => idx === pos || sv == null || v.options[idx] === sv)
        );
        const available = variants.some(v =>
          v.available &&
          v.options[pos] === input.value &&
          selected.every((sv, idx) => idx === pos || sv == null || v.options[idx] === sv)
        );
        wrapper?.classList.toggle('is-unavailable', !exists);
        wrapper?.classList.toggle('is-sold-out', exists && !available);
        input.disabled = !exists || !available;
      });

      selects.forEach(select => {
        const pos = Number(select.dataset.position) - 1;
        [...select.options].forEach(opt => {
          const exists = variants.some(v =>
            v.options[pos] === opt.value &&
            selected.every((sv, idx) => idx === pos || sv == null || v.options[idx] === sv)
          );
          const available = variants.some(v =>
            v.available &&
            v.options[pos] === opt.value &&
            selected.every((sv, idx) => idx === pos || sv == null || v.options[idx] === sv)
          );
          opt.disabled = !exists || !available;
        });
      });
    }

    function syncGallery(variant) {
      if (!variant || root.dataset.variantMediaSync !== 'true') return;
      const mediaId = variant.featured_media?.id || variant.featured_image?.id;
      if (!mediaId || window.innerWidth > 989) return;
      const gallery = root.querySelector('[data-k2-gallery]');
      const item = root.querySelector(`[data-k2-media-id="${mediaId}"]`);
      if (!gallery || !item) return;
      galleryScrollTo(gallery, item, reducedMotion ? 'auto' : 'smooth');
    }

    function setVariant(variant, syncControls = false) {
      if (!variant) return;
      currentVariant = variant;
      if (idInput) idInput.value = variant.id;

      if (syncControls) {
        variant.options.forEach((value, idx) => {
          root.querySelectorAll(`[data-k2-option-radio][data-position="${idx + 1}"]`).forEach(r => {
            r.checked = r.value === value;
          });
          const select = root.querySelector(`[data-k2-option-select][data-position="${idx + 1}"]`);
          if (select) select.value = value;
        });
      }

      root.querySelectorAll('[data-k2-selected-option]').forEach(label => {
        const idx = Number(label.dataset.k2SelectedOption) - 1;
        label.textContent = variant.options[idx] || '';
      });

      if (price) price.textContent = money(variant.price, fmt);

      if (compare) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compare.textContent = money(variant.compare_at_price, fmt);
          compare.hidden = false;
        } else {
          compare.textContent = '';
          compare.hidden = true;
        }
      }

      if (save) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          const amount = variant.compare_at_price - variant.price;
          const savingsValue = save.dataset.savingsMode !== 'amount'
            ? `${Math.round(amount * 100 / variant.compare_at_price)}%`
            : money(amount, fmt);
          const prefix = save.dataset.prefix === undefined ? 'Save' : save.dataset.prefix;
          save.textContent = `${prefix} ${savingsValue}`.trim();
          save.hidden = false;
        } else {
          save.hidden = true;
        }
      }

      const available = !!variant.available;

      if (mainAtc) {
        mainAtc.disabled = !available;
        const text = mainAtc.querySelector('[data-k2-main-atc-text]');
        if (text) text.textContent = available ? mainAtc.dataset.addText : mainAtc.dataset.soldOutText;
      }

      const stickySelect = root.querySelector('[data-k2-sticky-select]');
      if (stickySelect) stickySelect.value = String(variant.id);

      const stickyVariant = root.querySelector('[data-k2-sticky-variant]');
      if (stickyVariant) stickyVariant.textContent = variant.title;

      const stickyPrice = root.querySelector('[data-k2-sticky-price]');
      if (stickyPrice) stickyPrice.textContent = money(variant.price, fmt);

      const stickyAtc = root.querySelector('[data-k2-sticky-atc]');
      if (stickyAtc) {
        stickyAtc.disabled = !available;
        const text = stickyAtc.querySelector('[data-k2-sticky-atc-text]');
        if (text) text.textContent = available ? stickyAtc.dataset.addText : stickyAtc.dataset.soldOutText;
      }

      updateOptionStates();
      syncGallery(variant);

      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url.toString());
      } catch (e) {}

      root.dispatchEvent(new CustomEvent('keith:variant-change', {
        bubbles: true,
        detail: { variant }
      }));
    }

    async function addCurrentProduct(sourceButton) {
      if (!form || isAdding || !currentVariant?.available) return;
      isAdding = true;

      const buttons = [mainAtc, root.querySelector('[data-k2-sticky-atc]')].filter(Boolean);
      const originals = buttons.map(btn => ({
        btn,
        text: btn.querySelector('span')?.textContent || btn.textContent
      }));

      buttons.forEach(btn => {
        btn.disabled = true;
        const span = btn.querySelector('span');
        if (span) span.textContent = 'ADDING...';
      });

      try {
        let formData = new FormData(form);
        formData.set('id', String(currentVariant.id));
        const quantity = root.querySelector('[data-k2-qty]');
        if (quantity) formData.set('quantity', String(Math.max(1, Number(quantity.value || 1))));

        if (window.KeithPairings) formData = window.KeithPairings.prepare(root, formData);

        // Request the same HTML fragments the Pipeline drawer already uses.
        formData.set('sections', 'api-cart-items,api-cart-subtotal');
        formData.set('sections_url', window.theme?.routes?.root_url || window.Shopify?.routes?.root || '/');

        const addUrl = `${window.Shopify?.routes?.root || '/'}cart/add.js`;
        const response = await fetch(addUrl, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData
        });

        // A bundled-section HTTP 400 can still accompany a successful cart add.
        // Show the authoritative cart instead of inviting a duplicate submission.
        if (response.status === 400) {
          window.location.assign(window.theme?.routes?.cart || `${window.Shopify?.routes?.root || '/'}cart`);
          return;
        }
        if (!response.ok) {
          let message = 'Unable to add this item to cart.';
          try {
            const data = await response.json();
            message = data.description || data.message || message;
          } catch (e) {}
          throw new Error(message);
        }

        let item;
        try {
          item = await response.json();
        } catch (error) {
          // The POST succeeded even if its response body could not be decoded.
          window.location.assign(window.theme?.routes?.cart || `${window.Shopify?.routes?.root || '/'}cart`);
          return;
        }
        const opened = await openThemeCartDrawer(item);
        if (!opened) return;

        buttons.forEach(btn => {
          const span = btn.querySelector('span');
          if (span) span.textContent = 'ADDED';
        });

        setTimeout(() => {
          originals.forEach(({ btn }) => {
            const span = btn.querySelector('span');
            if (span) span.textContent = currentVariant?.available ? btn.dataset.addText : btn.dataset.soldOutText;
          });
        }, 1100);
      } catch (error) {
        console.warn('[Keith] Add to cart failed:', error);
        emit('keith:cart-error', { error });
        originals.forEach(({ btn }) => {
          const span = btn.querySelector('span');
          if (span) span.textContent = 'TRY AGAIN';
        });
        setTimeout(() => {
          originals.forEach(({ btn }) => {
            const span = btn.querySelector('span');
            if (span) span.textContent = currentVariant?.available ? btn.dataset.addText : btn.dataset.soldOutText;
          });
        }, 1600);
      } finally {
        isAdding = false;
        buttons.forEach(btn => { btn.disabled = !currentVariant?.available; });
      }
    }

    radios.forEach(input => input.addEventListener('change', () => setVariant(getVariant(), false)));
    selects.forEach(select => select.addEventListener('change', () => setVariant(getVariant(), false)));

    const qty = root.querySelector('[data-k2-qty]');
    root.querySelector('[data-k2-qty-minus]')?.addEventListener('click', () => {
      if (qty) qty.value = Math.max(1, Number(qty.value || 1) - 1);
    });
    root.querySelector('[data-k2-qty-plus]')?.addEventListener('click', () => {
      if (qty) qty.value = Math.max(1, Number(qty.value || 1) + 1);
    });

    const stickySelect = root.querySelector('[data-k2-sticky-select]');
    stickySelect?.addEventListener('change', () => {
      const variant = variants.find(v => String(v.id) === stickySelect.value);
      if (variant) setVariant(variant, true);
    });

    form?.addEventListener('submit', event => {
      const submitter = event.submitter;
      if (submitter && submitter.name && submitter.name !== 'add') return;
      event.preventDefault();
      addCurrentProduct(submitter || mainAtc);
    });

    root.querySelector('[data-k2-sticky-atc]')?.addEventListener('click', () => addCurrentProduct(root.querySelector('[data-k2-sticky-atc]')));

    const sticky = root.querySelector('[data-k2-sticky-cart]');
    if (sticky && mainAtc) {
      const syncStickyHeight = () => {
        if (!sticky.classList.contains('is-visible')) return;
        document.documentElement.style.setProperty('--k2-sticky-cart-height', `${sticky.offsetHeight}px`);
      };
      const observer = new IntersectionObserver(entries => {
        const e = entries[0];
        const show = !e.isIntersecting && e.boundingClientRect.top < 0;
        sticky.classList.toggle('is-visible', show);
        sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
        document.body.classList.toggle('k2-sticky-cart-visible', show);
        if (show) requestAnimationFrame(syncStickyHeight);
      }, { threshold: 0 });
      observer.observe(mainAtc);
      window.addEventListener('resize', syncStickyHeight, { passive: true });
    }

    initChoiceGuides(root, variants, () => currentVariant, setVariant);
    initCountdowns(root);
    initGallery(root);

    root.querySelectorAll('[data-k2-accordion][data-single="true"] details').forEach(detail => {
      detail.addEventListener('toggle', () => {
        if (!detail.open) return;
        detail.closest('[data-k2-accordion]')?.querySelectorAll('details[open]').forEach(other => {
          if (other !== detail) other.removeAttribute('open');
        });
      });
    });

    if (currentVariant) setVariant(currentVariant, true);
  }

  function initChoiceGuides(root, variants, getCurrentVariant, setVariant) {
    root.querySelectorAll('[data-k2-choice-open]').forEach(trigger => {
      const id = trigger.dataset.k2ChoiceOpen;
      const modal = root.querySelector(`[data-k2-choice-modal="${id}"]`);
      if (!modal || modal.dataset.k2ChoiceReady === '1') return;
      modal.dataset.k2ChoiceReady = '1';

      let lastFocus = null;

      const open = event => {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        lastFocus = document.activeElement;
        modal.hidden = false;
        document.documentElement.style.overflow = 'hidden';
        requestAnimationFrame(() => {
          modal.querySelector('.k2-choice-modal__close')?.focus({ preventScroll: true });
        });
      };

      const close = () => {
        modal.hidden = true;
        document.documentElement.style.overflow = '';
        lastFocus?.focus?.({ preventScroll: true });
      };

      trigger.addEventListener('click', open);
      modal.querySelectorAll('[data-k2-choice-close]').forEach(btn => {
        btn.addEventListener('click', event => {
          event.preventDefault();
          event.stopPropagation();
          close();
        });
      });

      modal.querySelectorAll('[data-k2-choice-value]').forEach(btn => {
        btn.addEventListener('click', () => {
          const value = btn.dataset.k2ChoiceValue;
          const normalizedValue = normalizeOptionValue(value);
          const pos = Math.max(0, Number(modal.dataset.k2ChoiceOptionPosition || 1) - 1);
          const current = getCurrentVariant();
          let match = null;

          const matchesChoice = variant => {
            const optionValue = normalizeOptionValue(variant.options?.[pos]);
            return (
              optionValue === normalizedValue ||
              optionValue.startsWith(normalizedValue) ||
              normalizedValue.startsWith(optionValue)
            );
          };

          if (current) {
            match = variants.find(v =>
              matchesChoice(v) &&
              v.options.every((opt, idx) => idx === pos || opt === current.options[idx])
            );
          }

          if (!match) match = variants.find(v => matchesChoice(v) && v.available);
          if (!match) match = variants.find(v => matchesChoice(v));

          if (match) {
            setVariant(match, true);
            close();
          } else {
            btn.classList.add('is-error');
            setTimeout(() => btn.classList.remove('is-error'), 1200);
          }
        });
      });

      modal.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
          close();
          return;
        }

        if (event.key !== 'Tab') return;

        const focusable = [...modal.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )].filter(el => !el.hidden);

        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    });
  }

  function initCountdowns(scope) {
    scope.querySelectorAll('[data-k2-countdown]').forEach(el => {
      if (el.dataset.k2CountdownReady === '1') return;
      el.dataset.k2CountdownReady = '1';
      const end = Date.parse(el.dataset.end || '');
      if (!Number.isFinite(end)) return;

      const expiredBehavior = el.dataset.expiredBehavior || 'hide';
      const expiredMessage = el.dataset.expiredMessage || 'Offer ended';
      const fields = {
        d: el.querySelector('[data-k2-days]'),
        h: el.querySelector('[data-k2-hours]'),
        m: el.querySelector('[data-k2-minutes]'),
        s: el.querySelector('[data-k2-seconds]')
      };

      const tick = () => {
        const diff = end - Date.now();
        if (diff <= 0) {
          if (expiredBehavior === 'hide') el.hidden = true;
          else {
            const msg = el.querySelector('[data-k2-countdown-title]');
            if (msg) msg.textContent = expiredMessage;
            Object.values(fields).forEach(f => { if (f) f.textContent = '00'; });
          }
          return false;
        }

        const total = Math.floor(diff / 1000);
        if (fields.d) fields.d.textContent = String(Math.floor(total / 86400)).padStart(2, '0');
        if (fields.h) fields.h.textContent = String(Math.floor((total % 86400) / 3600)).padStart(2, '0');
        if (fields.m) fields.m.textContent = String(Math.floor((total % 3600) / 60)).padStart(2, '0');
        if (fields.s) fields.s.textContent = String(total % 60).padStart(2, '0');
        return true;
      };

      tick();
      const timer = setInterval(() => {
        if (!tick()) clearInterval(timer);
      }, 1000);
    });
  }

  function initGallery(root) {
    const gallery = root.querySelector('[data-k2-gallery]');
    if (!gallery || gallery.dataset.k2GalleryReady === '1') return;
    gallery.dataset.k2GalleryReady = '1';

    const items = [...gallery.querySelectorAll('.k2-gallery__item')];
    const bars = [...root.querySelectorAll('[data-k2-gallery-bar]')];
    const count = root.querySelector('[data-k2-gallery-count]');
    if (!items.length) return;

    let active = 0;
    let pausedUntil = 0;
    let galleryVisible = true;
    let raf = 0;

    const zoomTriggers = [...gallery.querySelectorAll('[data-k2-gallery-zoom-trigger]')];
    const lightbox = root.querySelector('[data-k2-gallery-lightbox]');
    if (zoomTriggers.length && lightbox) {
      const zoomImage = lightbox.querySelector('[data-k2-gallery-zoom-image]');
      const zoomStage = lightbox.querySelector('[data-k2-gallery-zoom-stage]');
      const zoomCount = lightbox.querySelector('[data-k2-gallery-zoom-count]');
      const prevButton = lightbox.querySelector('[data-k2-gallery-zoom-prev]');
      const nextButton = lightbox.querySelector('[data-k2-gallery-zoom-next]');
      const closeButtons = [...lightbox.querySelectorAll('[data-k2-gallery-zoom-close]')];
      let zoomIndex = 0;
      let returnFocus = null;
      let touchStartX = 0;

      /* Keep the close control attached to the visible image rather than the
         browser corner. The image can be portrait, landscape, resized or
         constrained by the viewport, so its rendered rectangle is measured
         each time instead of relying on a fixed CSS offset. */
      const positionCloseButton = () => {
        try {
          if (lightbox.hidden || !zoomImage?.src) return;
          const rect = zoomImage.getBoundingClientRect();
          if (!rect.width || !rect.height) return;

          const inset = window.innerWidth <= 749 ? 10 : 14;
          const buttonSize = window.innerWidth <= 749 ? 40 : 44;
          const top = Math.max(inset, Math.min(window.innerHeight - buttonSize - inset, rect.top + inset));
          const right = Math.max(inset, Math.min(window.innerWidth - buttonSize - inset, window.innerWidth - rect.right + inset));

          lightbox.style.setProperty('--k2-lightbox-close-top', `${Math.round(top)}px`);
          lightbox.style.setProperty('--k2-lightbox-close-right', `${Math.round(right)}px`);
        } catch (error) {
          console.warn('[KEITH] Unable to reposition the gallery close button.', error);
        }
      };

      const renderZoom = index => {
        zoomIndex = (index + zoomTriggers.length) % zoomTriggers.length;
        const trigger = zoomTriggers[zoomIndex];
        zoomImage.classList.remove('is-zoomed');
        zoomStage?.scrollTo?.({ top: 0, left: 0, behavior: 'auto' });
        zoomImage.src = trigger.dataset.k2ZoomSrc;
        zoomImage.alt = trigger.dataset.k2ZoomAlt || '';
        if (zoomCount) zoomCount.textContent = `${zoomIndex + 1} / ${zoomTriggers.length}`;
        const hasMultiple = zoomTriggers.length > 1;
        if (prevButton) prevButton.hidden = !hasMultiple;
        if (nextButton) nextButton.hidden = !hasMultiple;
        if (zoomImage.complete) requestAnimationFrame(positionCloseButton);
      };

      const openZoom = index => {
        returnFocus = document.activeElement;

        /* Move the viewer outside Product Main before opening it. Some theme
           wrappers create their own fixed-position/stacking context, which
           otherwise lets the sticky header cover the close button. */
        if (lightbox.parentElement !== document.body) {
          const lightboxStyles = window.getComputedStyle(lightbox);
          const backdropColor = lightboxStyles.getPropertyValue('--k2-zoom-backdrop').trim();
          if (backdropColor) lightbox.style.setProperty('--k2-zoom-backdrop', backdropColor);
          document.body.appendChild(lightbox);
        }

        lightbox.hidden = false;
        lightbox.setAttribute('aria-hidden', 'false');
        document.body.classList.add('k2-lightbox-open');

        /* Rendering comes after the layer is visible. If image positioning
           ever fails, it must not prevent the viewer itself from opening. */
        try {
          renderZoom(index);
        } catch (error) {
          console.error('[KEITH] Unable to render the selected gallery image.', error);
        }
        requestAnimationFrame(() => {
          positionCloseButton();
          lightbox.querySelector('.k2-gallery-lightbox__close')?.focus({ preventScroll: true });
        });
      };

      const closeZoom = () => {
        lightbox.hidden = true;
        lightbox.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('k2-lightbox-open');
        zoomImage.classList.remove('is-zoomed');
        zoomImage.removeAttribute('src');
        returnFocus?.focus?.({ preventScroll: true });
      };

      zoomTriggers.forEach((trigger, index) => trigger.addEventListener('click', event => {
        event.preventDefault();
        openZoom(index);
      }));
      closeButtons.forEach(button => button.addEventListener('click', closeZoom));
      prevButton?.addEventListener('click', () => renderZoom(zoomIndex - 1));
      nextButton?.addEventListener('click', () => renderZoom(zoomIndex + 1));
      zoomImage?.addEventListener('load', () => requestAnimationFrame(positionCloseButton));
      window.addEventListener('resize', positionCloseButton, { passive: true });
      zoomImage?.addEventListener('click', () => {
        zoomImage.classList.toggle('is-zoomed');
        if (!zoomImage.classList.contains('is-zoomed')) zoomStage.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        requestAnimationFrame(positionCloseButton);
      });
      zoomStage?.addEventListener('touchstart', event => {
        if (event.touches.length === 1) touchStartX = event.touches[0].clientX;
      }, { passive: true });
      zoomStage?.addEventListener('touchend', event => {
        if (zoomImage.classList.contains('is-zoomed') || !event.changedTouches.length) return;
        const delta = event.changedTouches[0].clientX - touchStartX;
        if (Math.abs(delta) < 55) return;
        renderZoom(zoomIndex + (delta < 0 ? 1 : -1));
      }, { passive: true });
      lightbox.addEventListener('keydown', event => {
        if (event.key === 'Escape') closeZoom();
        if (event.key === 'ArrowLeft') renderZoom(zoomIndex - 1);
        if (event.key === 'ArrowRight') renderZoom(zoomIndex + 1);
        if (event.key === 'Tab') {
          const focusable = [...lightbox.querySelectorAll('button:not([hidden]):not(:disabled)')];
          if (!focusable.length) return;
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }
      });
    }

    const setActive = index => {
      active = Math.max(0, Math.min(items.length - 1, index));
      items.forEach((item, i) => { if (i !== active) item.querySelectorAll('video').forEach(video => video.pause()); });
      bars.forEach((bar, i) => bar.classList.toggle('is-active', i === active));
      if (count) count.textContent = `${active + 1} / ${items.length}`;
    };

    bars.forEach((bar, i) => {
      bar.addEventListener('click', () => {
        pausedUntil = Date.now() + 10000;
        galleryScrollTo(gallery, items[i], reducedMotion ? 'auto' : 'smooth');
        setActive(i);
      });
    });

    gallery.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const center = gallery.scrollLeft + gallery.clientWidth / 2;
        let best = 0;
        let dist = Infinity;
        items.forEach((item, i) => {
          const c = item.offsetLeft + item.offsetWidth / 2;
          const d = Math.abs(c - center);
          if (d < dist) {
            dist = d;
            best = i;
          }
        });
        setActive(best);
      });
    }, { passive: true });

    ['pointerdown', 'touchstart', 'wheel'].forEach(evt => {
      gallery.addEventListener(evt, () => {
        pausedUntil = Date.now() + 10000;
      }, { passive: true });
    });

    const visibilityObserver = new IntersectionObserver(entries => {
      galleryVisible = !!entries[0]?.isIntersecting;
    }, { threshold: 0.15 });
    visibilityObserver.observe(gallery);

    setActive(0);

    if (root.dataset.galleryAutoplay === 'true' && !reducedMotion) {
      const interval = Math.max(4000, Number(root.dataset.galleryInterval || 6000));
      setInterval(() => {
        if (
          window.innerWidth > 989 ||
          document.hidden ||
          !galleryVisible ||
          [...gallery.querySelectorAll('video')].some(video => !video.paused && !video.ended) ||
          Date.now() < pausedUntil ||
          items.length < 2
        ) return;

        const next = (active + 1) % items.length;
        galleryScrollTo(gallery, items[next], 'smooth');
        setActive(next);
      }, interval);
    }
  }


  function initSpectrum(root) {
    if (!root || root.dataset.k2SpectrumReady === '1') return;
    root.dataset.k2SpectrumReady = '1';

    const track = root.querySelector('[data-k2-spectrum-track]');
    const items = [...root.querySelectorAll('[data-k2-spectrum-item]')];
    if (!track || !items.length) return;

    const requestedIndex = Math.max(1, Number(track.dataset.defaultIndex || 1)) - 1;
    let activeIndex = Math.min(requestedIndex, items.length - 1);

    const activate = index => {
      activeIndex = Math.max(0, Math.min(items.length - 1, index));
      items.forEach((item, i) => {
        item.classList.toggle('is-active', i === activeIndex);
        item.setAttribute('aria-current', i === activeIndex ? 'true' : 'false');
      });
    };

    const centerItem = (item, behavior = 'smooth') => {
      if (!item) return;
      const left = item.offsetLeft - Math.max(0, (track.clientWidth - item.offsetWidth) / 2);
      track.scrollTo({
        left: Math.max(0, left),
        behavior: reducedMotion ? 'auto' : behavior
      });
    };

    items.forEach((item, index) => {
      item.tabIndex = 0;

      item.addEventListener('click', () => {
        activate(index);
        centerItem(item);
      });

      item.addEventListener('keydown', event => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        activate(index);
        centerItem(item);
      });
    });

    let raf = 0;
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const center = track.scrollLeft + track.clientWidth / 2;
        let bestIndex = 0;
        let bestDistance = Infinity;

        items.forEach((item, index) => {
          const itemCenter = item.offsetLeft + item.offsetWidth / 2;
          const distance = Math.abs(itemCenter - center);
          if (distance < bestDistance) {
            bestDistance = distance;
            bestIndex = index;
          }
        });

        activate(bestIndex);
      });
    }, { passive: true });

    activate(activeIndex);

    document.addEventListener('shopify:block:select', event => {
      const item = event.target?.closest?.('[data-k2-spectrum-item]');
      if (!item || !root.contains(item)) return;
      const index = items.indexOf(item);
      if (index >= 0) {
        activate(index);
        centerItem(item, 'auto');
      }
    });
  }

  function initAnchor(root) {
    if (!root || root.dataset.k2AnchorReady === '1') return;
    root.dataset.k2AnchorReady = '1';
    const links = [...root.querySelectorAll('[data-k2-anchor-link]')];
    const normalize = value => String(value || '').trim().replace(/^#/, '').toLowerCase();
    const resolve = value => {
      const id = String(value || '').trim().replace(/^#/, '');
      const exact = document.getElementById(id);
      if (exact) return exact;
      const key = normalize(id);
      const aliases = { specs: ['product-specs', 'spec-comparison'], features: ['feature-showcase'], overview: ['Features'], faq: ['faqs'] };
      const ids = [...document.querySelectorAll('[id]')];
      return ids.find(el => normalize(el.id) === key) ||
        (aliases[key] || []).map(alias => document.getElementById(alias)).find(Boolean) || null;
    };
    const product = () => document.querySelector('[data-k2-product], [data-k2cp-main]');
    let frame = 0;
    const headerBottom = () => {
      let bottom = 0;
      document.querySelectorAll('[data-header-wrapper] .theme__header').forEach(header => {
        const rect = header.getBoundingClientRect();
        const style = getComputedStyle(header);
        if (rect.height && rect.top <= 1 && rect.bottom > 0 && style.visibility !== 'hidden' && style.display !== 'none') bottom = Math.max(bottom, rect.bottom);
      });
      return Math.min(bottom, window.innerHeight / 2);
    };
    const update = () => {
      frame = 0;
      if (!root.isConnected) return;
      const top = headerBottom();
      root.style.setProperty('--k2-anchor-top', `${top}px`);
      const pairs = links.map(link => ({ link, el: resolve(link.dataset.k2AnchorLink) }));
      pairs.forEach(({link, el}) => { link.hidden = !el; });
      const main = product();
      const show = pairs.some(pair => pair.el) && (!main || main.getBoundingClientRect().bottom <= top + 1);
      root.classList.toggle('is-visible', show);
      root.setAttribute('aria-hidden', String(!show));
      root.inert = !show;
      const line = top + root.getBoundingClientRect().height + 24;
      let current = pairs.find(pair => pair.el);
      pairs.forEach(pair => { if (pair.el && pair.el.getBoundingClientRect().top <= line) current = pair; });
      pairs.forEach(pair => {
        const active = pair === current;
        pair.link.classList.toggle('is-active', active);
        if (active) pair.link.setAttribute('aria-current', 'location');
        else pair.link.removeAttribute('aria-current');
      });
    };
    const requestUpdate = () => { if (!frame) frame = requestAnimationFrame(update); };
    links.forEach(link => link.addEventListener('click', event => {
      const target = resolve(link.dataset.k2AnchorLink);
      if (!target) return;
      event.preventDefault();
      const offset = headerBottom() + root.getBoundingClientRect().height + 12;
      window.scrollTo({top: Math.max(0, window.scrollY + target.getBoundingClientRect().top - offset), behavior: reducedMotion ? 'auto' : 'smooth'});
    }));
    window.addEventListener('scroll', requestUpdate, {passive:true});
    window.addEventListener('resize', requestUpdate, {passive:true});
    document.addEventListener('shopify:section:load', requestUpdate);
    let resizeObserver;
    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(requestUpdate);
      const main = product();
      if (main) resizeObserver.observe(main);
      document.querySelectorAll('[data-header-wrapper]').forEach(header => resizeObserver.observe(header));
    }
    const unload = event => {
      if (!event.target.contains(root)) return;
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
      document.removeEventListener('shopify:section:load', requestUpdate);
      document.removeEventListener('shopify:section:unload', unload);
      if (resizeObserver) resizeObserver.disconnect();
      cancelAnimationFrame(frame);
    };
    document.addEventListener('shopify:section:unload', unload);
    update();
  }


  function initImageSlider(root) {
    if (!root || root.dataset.k2Ready === '1') return;
    root.dataset.k2Ready = '1';

    const triggers = [...root.querySelectorAll('[data-k2-slide-trigger]')];
    const slides = [...root.querySelectorAll('[data-k2-slide]')];
    const track = root.querySelector('[data-k2-thumb-track]');
    const prev = root.querySelector('[data-k2-thumb-prev]');
    const next = root.querySelector('[data-k2-thumb-next]');
    const mobilePrev = root.querySelector('[data-k2-image-prev]');
    const mobileNext = root.querySelector('[data-k2-image-next]');
    const current = root.querySelector('[data-k2-image-current]');
    const swipeSurface = root.querySelector('.k2-image-content__slides');
    const dots = [...root.querySelectorAll('[data-k2-thumb-dot]')];
    let active = 0;
    let touchStartX = 0;
    let touchStartY = 0;

    const activate = (index, scrollThumb = true) => {
      active = Math.max(0, Math.min(triggers.length - 1, index));
      const trigger = triggers[active];
      if (!trigger) return;

      triggers.forEach((button, i) => button.classList.toggle('is-active', i === active));
      slides.forEach(slide => slide.classList.toggle('is-active', slide.dataset.k2Slide === trigger.dataset.k2SlideTrigger));
      dots.forEach((dot, i) => {
        const on = i === active;
        dot.classList.toggle('is-active', on);
        if (on) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      if (prev) prev.disabled = active === 0;
      if (next) next.disabled = active === triggers.length - 1;
      if (mobilePrev) mobilePrev.disabled = active === 0;
      if (mobileNext) mobileNext.disabled = active === triggers.length - 1;
      if (current) current.textContent = String(active + 1).padStart(2, '0');
      root.style.setProperty('--k2-image-progress', `${((active + 1) / triggers.length) * 100}%`);

      if (scrollThumb && track && track.scrollWidth > track.clientWidth) {
        const maxLeft = Math.max(0, track.scrollWidth - track.clientWidth);
        track.scrollTo({
          left: Math.min(trigger.offsetLeft, maxLeft),
          behavior: reducedMotion ? 'auto' : 'smooth'
        });
      }
    };

    triggers.forEach((button, index) => button.addEventListener('click', () => activate(index)));
    prev?.addEventListener('click', () => activate(active - 1));
    next?.addEventListener('click', () => activate(active + 1));
    mobilePrev?.addEventListener('click', () => activate(active - 1));
    mobileNext?.addEventListener('click', () => activate(active + 1));
    dots.forEach((dot, index) => dot.addEventListener('click', () => activate(index)));

    swipeSurface?.addEventListener('touchstart', event => {
      touchStartX = event.touches[0].clientX;
      touchStartY = event.touches[0].clientY;
    }, { passive: true });
    swipeSurface?.addEventListener('touchend', event => {
      const dx = event.changedTouches[0].clientX - touchStartX;
      const dy = event.changedTouches[0].clientY - touchStartY;
      if (Math.abs(dx) < 42 || Math.abs(dx) <= Math.abs(dy)) return;
      activate(active + (dx < 0 ? 1 : -1));
    }, { passive: true });

    document.addEventListener('shopify:block:select', event => {
      const trigger = event.target?.closest?.('[data-k2-slide-trigger]');
      if (!trigger || !root.contains(trigger)) return;
      const index = triggers.indexOf(trigger);
      if (index >= 0) activate(index, false);
    });

    activate(0, false);
  }


  function initFeatureShowcase(root) {
    if (!root || root.dataset.k2FeatureReady === '1') return;
    root.dataset.k2FeatureReady = '1';

    const track = root.querySelector('[data-k2-feature-track]');
    const cards = [...root.querySelectorAll('[data-k2-feature-card]')];
    if (!track || cards.length <= 3) return;

    const prev = root.querySelector('[data-k2-feature-prev]');
    const next = root.querySelector('[data-k2-feature-next]');
    const dots = [...root.querySelectorAll('[data-k2-feature-dot]')];

    let active = 0;
    let raf = 0;

    const clamp = index => Math.max(0, Math.min(cards.length - 1, index));

    const setActive = index => {
      active = clamp(index);

      dots.forEach((dot, i) => {
        const on = i === active;
        dot.classList.toggle('is-active', on);
        if (on) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      if (prev) prev.disabled = active <= 0;
      if (next) next.disabled = active >= cards.length - 1;
    };

    const scrollToCard = (index, behavior = 'smooth') => {
      const targetIndex = clamp(index);
      const card = cards[targetIndex];
      if (!card) return;

      track.scrollTo({
        left: Math.max(0, card.offsetLeft),
        behavior: reducedMotion ? 'auto' : behavior
      });

      setActive(targetIndex);
    };

    const nearestCard = () => {
      const left = track.scrollLeft;
      let best = 0;
      let distance = Infinity;

      cards.forEach((card, index) => {
        const d = Math.abs(card.offsetLeft - left);
        if (d < distance) {
          distance = d;
          best = index;
        }
      });

      return best;
    };

    prev?.addEventListener('click', () => scrollToCard(active - 1));
    next?.addEventListener('click', () => scrollToCard(active + 1));

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => scrollToCard(index));
    });

    track.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setActive(nearestCard()));
    }, { passive: true });

    document.addEventListener('shopify:block:select', event => {
      const card = event.target?.closest?.('[data-k2-feature-card]');
      if (!card || !root.contains(card)) return;

      const index = cards.indexOf(card);
      if (index >= 0) scrollToCard(index, 'auto');
    });

    setActive(0);
  }

  function initCompare(root) {
    if (!root || root.dataset.k2Ready === '1') return;
    root.dataset.k2Ready = '1';

    const tabs = [...root.querySelectorAll('[data-k2-compare-tab]')];
    const cards = [...root.querySelectorAll('[data-k2-compare-card]')];

    const activate = id => {
      tabs.forEach(t => {
        const on = t.dataset.k2CompareTab === id;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      cards.forEach(c => {
        c.hidden = c.dataset.k2CompareCard !== id;
      });
    };

    tabs.forEach(t => t.addEventListener('click', () => activate(t.dataset.k2CompareTab)));
    if (tabs[0]) activate(tabs[0].dataset.k2CompareTab);
  }

  function initFaq(root) {
    if (!root || root.dataset.k2Ready === '1') return;
    root.dataset.k2Ready = '1';
    if (root.dataset.single !== 'true') return;

    root.querySelectorAll('details').forEach(detail => {
      detail.addEventListener('toggle', () => {
        if (!detail.open) return;
        root.querySelectorAll('details[open]').forEach(other => {
          if (other !== detail) other.removeAttribute('open');
        });
      });
    });
  }

  const boot = scope => {
    (scope || document).querySelectorAll('[data-k2-anchor-nav]').forEach(initAnchor);
    /* Gallery is initialized independently and first. A malformed product
       JSON or an unrelated form error must never disable image enlargement. */
    (scope || document).querySelectorAll('[data-k2-product]').forEach(root => {
      initGallery(root);
      initProduct(root);
    });
    (scope || document).querySelectorAll('[data-k2-image-slider]').forEach(initImageSlider);
    (scope || document).querySelectorAll('[data-k2-feature-slider]').forEach(initFeatureShowcase);
    (scope || document).querySelectorAll('[data-k2-compare]').forEach(initCompare);
    (scope || document).querySelectorAll('[data-k2-faq]').forEach(initFaq);
    (scope || document).querySelectorAll('[data-k2-spectrum]').forEach(initSpectrum);
    initCountdowns(scope || document);
  };

  window.KeithSectionLibraryBoot = boot;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document));
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', e => boot(e.target));
})();
